import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, CheckSquare, Loader2, Calendar, ExternalLink } from 'lucide-react'
import { getFunctions, httpsCallable } from 'firebase/functions'

export interface PwfTask {
  id: number
  title: string
  projectId: number
  projectTitle: string
  duedate?: string
  status?: string
  assignedto?: string
}

import { pwfProjectUrl } from '../utils/pwfUrls'

function getDateStr(d: Date) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

export default function PwfTasks() {
  const navigate = useNavigate()
  const [tasks, setTasks] = useState<PwfTask[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [debug, setDebug] = useState<Record<string, unknown> | null>(null)

  const today = useMemo(() => getDateStr(new Date()), [])
  const tomorrow = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    return getDateStr(d)
  }, [])

  const stats = useMemo(() => {
    if (!tasks?.length) return { today: 0, tomorrow: 0, week: 0 }
    let todayCount = 0
    let tomorrowCount = 0
    for (const t of tasks) {
      const ds = t.duedate ? t.duedate.split("T")[0] : ""
      if (ds === today) todayCount++
      else if (ds === tomorrow) tomorrowCount++
    }
    return { today: todayCount, tomorrow: tomorrowCount, week: tasks.length }
  }, [tasks, today, tomorrow])

  const chartData = useMemo(() => {
    if (!tasks?.length) return []
    const counts: Record<string, number> = {}
    for (let i = 0; i < 8; i++) {
      const d = new Date()
      d.setDate(d.getDate() + i)
      const key = getDateStr(d)
      counts[key] = 0
    }
    for (const t of tasks) {
      const ds = t.duedate ? t.duedate.split("T")[0] : ""
      if (ds in counts) counts[ds]++
    }
    return Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => ({
        date,
        label: new Date(date).toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short" }),
        count,
      }))
  }, [tasks])

  const maxCount = useMemo(() => Math.max(1, ...chartData.map((d) => d.count)), [chartData])

  useEffect(() => {
    async function fetchTasks() {
      try {
        setError(null)
        const functions = getFunctions()
        const getPwfTasks = httpsCallable<unknown, { success: boolean; data?: PwfTask[] }>(
          functions,
          'getPwfTasksDueToday'
        )
        const result = await getPwfTasks({})
        const data = result.data as { success?: boolean; data?: PwfTask[]; debug?: Record<string, unknown> }
        if (data?.success && Array.isArray(data.data)) {
          setTasks(data.data)
          setDebug(data.debug || null)
        } else {
          setTasks([])
          setDebug(null)
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load PWF tasks'
        setError(message)
        setTasks([])
      } finally {
        setLoading(false)
      }
    }
    fetchTasks()
  }, [])

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Hub
        </button>

        <div className="card">
          <div className="mb-6">
            <div className="flex items-center gap-2">
              <CheckSquare className="w-6 h-6 text-emerald-600" />
              <h1 className="text-2xl font-bold text-gray-900">ProWorkflow Tasks Due Soon</h1>
            </div>
            <p className="text-sm text-gray-500 mt-1">Today, tomorrow & next week</p>
          </div>

          {!loading && !error && tasks && tasks.length > 0 && (
            <>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
                  <p className="text-sm font-medium text-emerald-700">Due today</p>
                  <p className="text-2xl font-bold text-emerald-900 mt-1">{stats.today}</p>
                </div>
                <div className="rounded-xl bg-amber-50 border border-amber-100 p-4">
                  <p className="text-sm font-medium text-amber-700">Due tomorrow</p>
                  <p className="text-2xl font-bold text-amber-900 mt-1">{stats.tomorrow}</p>
                </div>
                <div className="rounded-xl bg-sky-50 border border-sky-100 p-4">
                  <p className="text-sm font-medium text-sky-700">Next 7 days</p>
                  <p className="text-2xl font-bold text-sky-900 mt-1">{stats.week}</p>
                </div>
              </div>

              <div className="mb-6">
                <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Workload by day
                </h2>
                <div className="flex items-end gap-2 h-36">
                  {chartData.map((d) => {
                    const pct = maxCount > 0 ? (d.count / maxCount) * 100 : 0
                    return (
                      <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
                        <div className="w-full flex flex-col justify-end h-28">
                          <div
                            className="w-full rounded-t bg-emerald-500/80 hover:bg-emerald-500 transition-colors"
                            style={{ height: `${Math.max(8, pct)}%` }}
                            title={`${d.label}: ${d.count} task${d.count !== 1 ? "s" : ""}`}
                          />
                        </div>
                        <span className="text-xs text-gray-500 truncate w-full text-center" title={d.label}>
                          {d.label.split(" ")[0]}
                        </span>
                        <span className="text-xs font-medium text-gray-700">{d.count}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-gray-500 py-12">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span>Loading tasks…</span>
            </div>
          ) : error ? (
            <p className="text-sm text-amber-600 py-6">{error}</p>
          ) : !tasks || tasks.length === 0 ? (
            <div className="py-6 space-y-2">
              <p className="text-gray-500">No tasks due in the next week</p>
              {debug && (
                <details className="text-xs text-gray-400 mt-2">
                  <summary className="cursor-pointer hover:text-gray-600">Debug info</summary>
                  <pre className="mt-2 p-2 bg-gray-100 rounded overflow-auto max-h-32">
                    {JSON.stringify(debug, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ) : (
            <ul className="space-y-3">
              {tasks.map((task) => {
                const dueStr = task.duedate
                  ? new Date(task.duedate).toLocaleDateString("en-NZ", {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                  })
                  : "";
                return (
                  <li
                    key={`${task.projectId}-${task.id}`}
                    className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0"
                  >
                    <CheckSquare className="w-5 h-5 mt-0.5 text-emerald-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <a
                        href={pwfProjectUrl(task.projectId) ?? '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-gray-900 hover:text-emerald-600 inline-flex items-center gap-1.5 group"
                      >
                        {task.title}
                        <ExternalLink className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100" aria-hidden />
                      </a>
                      <p className="text-sm text-gray-500">
                        {task.projectTitle}
                        {dueStr && ` · Due ${dueStr}`}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </main>
    </div>
  )
}
